import React, { useState, useRef, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthModal from "./components/AuthModal";
import Startseite from "./pages/Startseite";
import Produkte from "./pages/Produkte";
import Kontakt from "./pages/Kontakt";
import Admin from "./pages/Admin";
import UeberUns from "./pages/UeberUns";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import Profil from "./pages/Profil";
import Molkerei from "./pages/Molkerei";
import Fahrer from "./pages/Fahrer";
import News from "./pages/News";
import Dienstplan from "./pages/Dienstplan";
import Schulmilch from "./pages/Schulmilch";
import Regiomaten from "./pages/Regiomaten";
import Betriebsurlaub from "./pages/Betriebsurlaub";
import Grossbestellungen from "./pages/Grossbestellungen";
import AusbildungJob from "./pages/AusbildungJob";
import FAQ from "./pages/FAQ";
import PasswortReset from "./pages/PasswortReset";
import "./App.css";

// ── Mehr-Dropdown ─────────────────────────────────────────
function NavDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Schließen bei Klick außerhalb
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const items = [
    { icon: "🏪", label: "Regiomaten",            href: "/regiomaten",       badge: null },
    { icon: "🏫", label: "Schule & Kindergarten", href: "/schulmilch",       badge: null },
    { icon: "📦", label: "Großbestellungen",       href: "/grossbestellungen", badge: null },
    { icon: "👷", label: "Ausbildung & Job",       href: "/ausbildung-job",   badge: null },
    { icon: "🏖️", label: "Betriebsurlaub",        href: "/betriebsurlaub",  badge: null },
    { icon: "❓", label: "Häufige Fragen",         href: "/faq",             badge: null },
    { icon: "✉️", label: "Kontakt",               href: "/kontakt",          badge: null },
    { icon: "🌿", label: "Über uns",              href: "/ueber-uns",        badge: null },
  ];

  return (
    <div className={`nav-dropdown${open ? " open" : ""}`} ref={ref}>
      <button className="nav-dropdown-trigger" onClick={() => setOpen(o => !o)}>
        Mehr <span className="chevron">▼</span>
      </button>
      <div className="nav-dropdown-menu">
        {items.map(item => item.href
          ? item.extern
            ? (
              <a key={item.label} href={item.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
                <span className="menu-icon">{item.icon}</span>
                {item.label}
                {item.badge && <span className="menu-soon">{item.badge}</span>}
              </a>
            ) : (
              <Link key={item.label} to={item.href} onClick={() => setOpen(false)}>
                <span className="menu-icon">{item.icon}</span>
                {item.label}
                {item.badge && <span className="menu-soon">{item.badge}</span>}
              </Link>
            )
          : (
            <button key={item.label} disabled style={{ opacity: 0.55, cursor: "not-allowed" }}>
              <span className="menu-icon">{item.icon}</span>
              {item.label}
              {item.badge && <span className="menu-soon">{item.badge}</span>}
            </button>
          )
        )}
      </div>
    </div>
  );
}

function Navbar() {
  const { user, logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener("open-auth-modal", handler);
    return () => window.removeEventListener("open-auth-modal", handler);
  }, []);

  const rolle = user?.rolle;
  const istFahrerBereich   = rolle === "Fahrer"   || rolle === "FahrerAdmin";
  const istMolkereiBereich = rolle === "Molkerei" || rolle === "MolkereiAdmin";

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <nav className="navbar">
        <Link to="/" style={{ textDecoration: "none", color: "white" }} className="nav-brand" onClick={closeMenu}>Milchhof Gehrke</Link>
        <div className={`nav-links${menuOpen ? " nav-links-open" : ""}`}>
          <Link to="/" onClick={closeMenu}>Startseite</Link>
          <Link to="/produkte" onClick={closeMenu}>Produkte</Link>
          <Link to="/news" onClick={closeMenu}>News</Link>
          <NavDropdown />
          {user && <Link to="/profil" onClick={closeMenu}>Profil</Link>}
          {istMolkereiBereich && <Link to="/molkerei" onClick={closeMenu}>🥛 Molkerei</Link>}
          {istFahrerBereich   && <Link to="/fahrer" onClick={closeMenu}>🚚 Lieferplan</Link>}
          {(istFahrerBereich || istMolkereiBereich || user?.isAdmin) &&
            <Link to="/dienstplan" onClick={closeMenu}>📋 Dienstplan</Link>}
          {user?.isAdmin && <Link to="/admin" onClick={closeMenu}>Admin</Link>}
          {/* Mobile-only: Auth-Bereich im Menü */}
          <div className="nav-mobile-auth">
            {user ? (
              <>
                <Link to="/profil" style={{ color: "white", textDecoration: "none" }} onClick={closeMenu}>👤 {user.vorname}</Link>
                <button onClick={() => { logout(); closeMenu(); }} className="btn"
                  style={{ background: "rgba(255,255,255,0.15)", color: "white", padding: "0.4rem 1rem", fontSize: "0.9rem" }}>
                  Abmelden
                </button>
              </>
            ) : (
              <button onClick={() => { setShowModal(true); closeMenu(); }} className="btn"
                style={{ background: "white", color: "#2e7d32", padding: "0.4rem 1rem", fontSize: "0.9rem", fontWeight: 700 }}>
                Anmelden
              </button>
            )}
          </div>
        </div>
        <div className="nav-right">
          {user ? (
            <>
              <span style={{ color: "#c8e6c9", fontSize: "0.9rem", cursor: "pointer" }}>
                <Link to="/profil" style={{ color: "#c8e6c9", textDecoration: "none" }}>
                  👤 {user.vorname}
                </Link>
              </span>
              <button onClick={logout} className="btn"
                style={{ background: "rgba(255,255,255,0.15)", color: "white", padding: "0.4rem 1rem", fontSize: "0.9rem" }}>
                Abmelden
              </button>
            </>
          ) : (
            <button onClick={() => setShowModal(true)} className="btn"
              style={{ background: "white", color: "#2e7d32", padding: "0.4rem 1rem", fontSize: "0.9rem", fontWeight: 700 }}>
              Anmelden
            </button>
          )}
        </div>
        <button className="nav-hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menü">
          {menuOpen ? "✕" : "☰"}
        </button>
      </nav>
      {showModal && <AuthModal onClose={() => setShowModal(false)} />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Navbar />
        <main>
          <Routes>
            <Route path="/"            element={<Startseite />} />
            <Route path="/produkte"    element={<Produkte />} />
            <Route path="/ueber-uns"   element={<UeberUns />} />
            <Route path="/kontakt"     element={<Kontakt />} />
            <Route path="/admin"       element={<Admin />} />
            <Route path="/impressum"   element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/profil"      element={<Profil />} />
            <Route path="/molkerei"    element={<Molkerei />} />
            <Route path="/fahrer"      element={<Fahrer />} />
            <Route path="/news"        element={<News />} />
            <Route path="/dienstplan"  element={<Dienstplan />} />
            <Route path="/schulmilch"  element={<Schulmilch />} />
            <Route path="/regiomaten"     element={<Regiomaten />} />
            <Route path="/betriebsurlaub"    element={<Betriebsurlaub />} />
            <Route path="/grossbestellungen" element={<Grossbestellungen />} />
            <Route path="/ausbildung-job"    element={<AusbildungJob />} />
            <Route path="/faq"               element={<FAQ />} />
            <Route path="/passwort-reset"    element={<PasswortReset />} />
          </Routes>
        </main>
        <footer className="footer">
          <p>© 2026 Milchbauernhof Gehrke · Frisch vom Hof</p>
          <p style={{ marginTop: "0.4rem", display: "flex", justifyContent: "center", gap: "1.5rem" }}>
            <Link to="/impressum"   style={{ color: "#a5d6a7", fontSize: "0.85rem" }}>Impressum</Link>
            <Link to="/datenschutz" style={{ color: "#a5d6a7", fontSize: "0.85rem" }}>Datenschutz</Link>
          </p>
        </footer>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
